import { useEffect, useRef, useState } from "react";
import {
  deleteCaregiverAuthoredProfileFact,
  setKnowledgeItemFactStatus,
  setKnowledgeItemProfileVisibility,
} from "../../../features/knowledgeItems";
import { getCareSourceById } from "../../../features/careSourceProcessing/careSourceLookupService";
import {
  buildProfileSourceViewerModel,
  getProfileSourceId,
} from "../../../features/profileContract/profileSourceViewer";

// Shared across every Knowledge-backed Profile chapter. This owns only the
// domain-agnostic plumbing: resolving which entry was tapped, overlaying
// permission onto the Profile Contract's sourceAction, resolving the opaque
// actionId against the live knowledgeFacts array, and tracking the explicit
// refresh state after a save. It knows nothing about how any one domain
// classifies or ranks facts - that stays in each domain builder file.

const findFactById = (knowledgeFacts, factId) => (
  (knowledgeFacts || []).find((fact) => fact.id === factId) || null
);

// Overlays *permission* onto the Profile Contract's sourceAction without
// touching classification (ownership/actionId never change here - only what
// the current user is allowed to do with it). This intentionally lives at the
// chapter-interaction boundary, not in any domain builder: the contract
// describes what a fact *is*, this describes what *this user* can do right now.
const resolvePermittedSourceAction = (sourceAction, { canEditKnowledge, canViewDocuments }) => {
  if (!sourceAction) return undefined;
  // Incomplete metadata (e.g. missing the opaque handle) must never route -
  // fail closed rather than guess.
  if (!sourceAction.actionId || !sourceAction.ownership) return undefined;

  if (sourceAction.ownership === "caregiver_fact") {
    if (canEditKnowledge) return sourceAction;
    return { ...sourceAction, actionLabel: "View details", isEditable: false };
  }

  if (sourceAction.ownership === "external_record") {
    if (canViewDocuments) return sourceAction;
    return {
      ...sourceAction,
      actionLabel: "Restricted",
      isAvailable: false,
      unavailableReason: "restricted",
    };
  }

  // manager_reference (currently only DM_07 medication/allergy facts) is a
  // pure navigation hint to another owning manager, not an editable Knowledge
  // fact - permission enforcement for what happens once there belongs to that
  // manager itself, not to this overlay.
  if (sourceAction.ownership === "manager_reference") {
    return sourceAction;
  }

  return undefined;
};

const withResolvedSourceActions = (chapter, permissions) => {
  if (!chapter?.handbook?.groups) return chapter;
  return {
    ...chapter,
    handbook: {
      ...chapter.handbook,
      groups: chapter.handbook.groups.map((group) => ({
        ...group,
        entries: (group.entries || []).map((entry) => ({
          ...entry,
          sourceAction: resolvePermittedSourceAction(entry.sourceAction, permissions),
        })),
      })),
    },
  };
};

export const useHandbookSourceInteraction = ({
  chapter,
  knowledgeFacts = [],
  canEditProfile = false,
  canViewDocuments = false,
  // Optional: called instead of opening a sheet when a manager_reference entry
  // is tapped (currently only DM_07). Receives (destination, actionId).
  onNavigateToManager,
}) => {
  const [activeEntry, setActiveEntry] = useState(null);
  // Explicit refresh states rather than a boolean + hidden timeout, so the UI
  // never implies "current" when it isn't: idle -> saved_waiting_for_refresh
  // -> refreshed (idle) or refresh_delayed (still idle-looking but different
  // copy) if the subscription hasn't confirmed within a reasonable window.
  const [refreshState, setRefreshState] = useState("idle");
  const [pendingFact, setPendingFact] = useState(null); // { factId, text }
  const [visibilityError, setVisibilityError] = useState("");
  const [lifecycleError, setLifecycleError] = useState("");
  const [activeSource, setActiveSource] = useState(null);
  const [isSourceLoading, setIsSourceLoading] = useState(false);
  const delayTimeoutRef = useRef(null);

  useEffect(() => () => {
    if (delayTimeoutRef.current) clearTimeout(delayTimeoutRef.current);
  }, []);

  useEffect(() => {
    if (!activeEntry || activeEntry.sourceAction.ownership !== "external_record") {
      setActiveSource(null);
      setIsSourceLoading(false);
      return undefined;
    }
    const fact = findFactById(knowledgeFacts, activeEntry.sourceAction.actionId);
    const sourceId = getProfileSourceId(fact);
    if (!sourceId) return undefined;

    let active = true;
    setIsSourceLoading(true);
    getCareSourceById(sourceId)
      .then((source) => {
        if (active) {
          setActiveSource(source);
          setIsSourceLoading(false);
        }
      })
      .catch(() => {
        if (active) {
          setActiveSource(null);
          setIsSourceLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [activeEntry, knowledgeFacts]);

  useEffect(() => {
    if (!pendingFact) return undefined;
    const updatedFact = (knowledgeFacts || []).find((fact) => fact.id === pendingFact.factId);
    if (updatedFact && (updatedFact.summary || "").trim() === pendingFact.text.trim()) {
      setRefreshState("idle");
      setPendingFact(null);
      if (delayTimeoutRef.current) {
        clearTimeout(delayTimeoutRef.current);
        delayTimeoutRef.current = null;
      }
    }
    return undefined;
  }, [knowledgeFacts, pendingFact]);

  const handleEntryAction = (entry, triggerElement, options = {}) => {
    if (!entry?.sourceAction?.actionId || !entry.sourceAction.ownership) return;
    if (entry.sourceAction.ownership === "manager_reference") {
      // No sheet to open here - this hands off to another owning manager
      // (e.g. Medication Manager) entirely outside the Handbook's read-only
      // presentation, so there is no local edit/view state to track.
      onNavigateToManager?.(entry.sourceAction.destination, entry.sourceAction.actionId);
      return;
    }
    setActiveSource(null);
    setIsSourceLoading(false);
    setActiveEntry({
      entry,
      sourceAction: entry.sourceAction,
      triggerElement,
      readOnly: Boolean(options.readOnly),
      fullEntry: Boolean(options.fullEntry),
    });
  };

  const closeActiveEntry = () => {
    activeEntry?.triggerElement?.focus?.();
    setActiveEntry(null);
  };

  const handleSaved = (factId, text) => {
    setActiveEntry((current) => {
      current?.triggerElement?.focus?.();
      return null;
    });
    setRefreshState("saved_waiting_for_refresh");
    setPendingFact({ factId, text });
    if (delayTimeoutRef.current) clearTimeout(delayTimeoutRef.current);
    // Cosmetic-only: changes the *message* after a delay, it never clears the
    // pending watch or claims the refresh completed. The knowledgeFacts effect
    // above is the only thing that ever moves refreshState back to "idle".
    delayTimeoutRef.current = setTimeout(() => {
      setRefreshState((current) => (current === "saved_waiting_for_refresh" ? "refresh_delayed" : current));
    }, 8000);
  };

  const handleHideFromProfile = async (entry) => {
    if (!canEditProfile || !entry?.knowledgeFactId) return;
    setVisibilityError("");
    try {
      await setKnowledgeItemProfileVisibility(entry.knowledgeFactId, true);
    } catch (error) {
      setVisibilityError(error?.message || "We couldn't hide this from Profile right now.");
    }
  };

  const handleMarkOutdated = async (entry) => {
    if (!canEditProfile || !entry?.knowledgeFactId) return;
    setLifecycleError("");
    try {
      await setKnowledgeItemFactStatus(entry.knowledgeFactId, "outdated");
    } catch (error) {
      setLifecycleError(error?.message || "We couldn't mark this information as outdated right now.");
    }
  };

  const handleDelete = async (entry) => {
    if (!canEditProfile || !entry?.knowledgeFactId) return;
    setLifecycleError("");
    const fact = findFactById(knowledgeFacts, entry.knowledgeFactId);
    try {
      await deleteCaregiverAuthoredProfileFact(fact);
    } catch (error) {
      setLifecycleError(error?.message || "We couldn't delete this information right now.");
    }
  };

  const resolvedChapter = withResolvedSourceActions(chapter, {
    canEditKnowledge: canEditProfile,
    canViewDocuments,
  });

  const activeFact = activeEntry ? findFactById(knowledgeFacts, activeEntry.sourceAction.actionId) : null;
  const isEditSheetOpen = Boolean(activeEntry) && activeEntry.sourceAction.ownership === "caregiver_fact";
  const isViewSourceOpen = Boolean(activeEntry) && activeEntry.sourceAction.ownership === "external_record";
  const viewSource = isViewSourceOpen
    ? buildProfileSourceViewerModel({
      fact: activeFact,
      sourceAction: activeEntry.sourceAction,
      source: activeSource,
    })
    : { title: "", metadataLabel: "", body: "", imageUrl: "", documentUrl: "", documentName: "" };
  const isViewSourceAvailable = isViewSourceOpen
    && Boolean(activeEntry.sourceAction.isAvailable)
    && Boolean(viewSource.body || viewSource.imageUrl || viewSource.documentUrl);
  const viewSourceUnavailableMessage = activeEntry?.sourceAction?.unavailableReason === "restricted"
    ? "You don't have permission to view this source."
    : undefined;

  const refreshBanner = refreshState === "saved_waiting_for_refresh"
    ? "Updating…"
    : refreshState === "refresh_delayed"
      ? "Saved. The Handbook is still catching up."
      : null;

  return {
    resolvedChapter,
    handleEntryAction,
    handleHideFromProfile: canEditProfile ? handleHideFromProfile : undefined,
    handleMarkOutdated: canEditProfile ? handleMarkOutdated : undefined,
    handleDelete: canEditProfile ? handleDelete : undefined,
    visibilityError,
    lifecycleError,
    refreshBanner,
    editSheetProps: {
      open: isEditSheetOpen,
      entry: activeEntry?.entry,
      fact: activeFact,
      readOnly: Boolean(activeEntry?.readOnly) || !activeEntry?.sourceAction?.isEditable,
      fullEntry: Boolean(activeEntry?.fullEntry),
      onClose: closeActiveEntry,
      onSaved: handleSaved,
    },
    viewSourceProps: {
      open: isViewSourceOpen,
      title: viewSource.title,
      metadataLabel: viewSource.metadataLabel,
      body: viewSource.body,
      imageUrl: viewSource.imageUrl,
      imageAlt: viewSource.title,
      documentUrl: viewSource.documentUrl,
      documentName: viewSource.documentName,
      isLoading: isSourceLoading,
      isAvailable: isViewSourceAvailable,
      unavailableMessage: viewSourceUnavailableMessage,
      onClose: closeActiveEntry,
    },
  };
};
